/**
 * Report Management Component - Admin Dashboard
 * Allows admins to generate and download reports for orders and users
 */

import React, { useState } from 'react';
import { 
  FileText, 
  Download, 
  Calendar, 
  Users, 
  ShoppingCart,
  Loader2,
  AlertCircle,
  CheckCircle,
  IndianRupee,
  TrendingUp,
  Coins,
  Receipt
} from 'lucide-react';
import reportService from '../../services/reportService';
import { getMethodName, getInstrumentName } from '../../lib/paymentLabels';

/**
 * Money to the paisa, for the columns that have to be seen to add up.
 *
 * en-IN's default caps at three fraction digits and drops trailing zeros, so a
 * tax column left to it prints ₹1,234.567 on one row and ₹30 on the next. The
 * rupee-value columns keep that default; anything on the taxable-amount / GST
 * side is pinned to two so Taxable + GST visibly reconciles to Total Order Value.
 */
const formatPaise = (value) =>
  Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const ReportManagement = () => {
  const [reportType, setReportType] = useState('orders');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [previewData, setPreviewData] = useState(null);

  const handleGenerateReport = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    setPreviewData(null);

    try {
      let result;
      if (reportType === 'orders') {
        result = await reportService.getOrdersReport(startDate, endDate);
      } else if (reportType === 'users') {
        result = await reportService.getUsersReport(startDate, endDate);
      } else if (reportType === 'total-sales') {
        result = await reportService.getTotalSalesReport(startDate, endDate);
      } else if (reportType === 'bmf-coins') {
        result = await reportService.getBMFCoinsReport(startDate, endDate);
      } else if (reportType === 'refunds') {
        result = await reportService.getRefundsReport(startDate, endDate);
      }

      if (result.success) {
        setPreviewData(result.data);
        let count = 0;
        if (reportType === 'orders' || reportType === 'total-sales') {
          count = result.data.orders.length;
        } else if (reportType === 'bmf-coins') {
          count = result.data.transactions.length;
        } else if (reportType === 'refunds') {
          count = result.data.refunds.length;
        } else {
          count = result.data.users.length;
        }
        setSuccess(`Found ${count} records`);
      } else {
        setError(result.error || 'Failed to generate report');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!previewData) return;

    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `${reportType}-report-${dateStr}.csv`;

    if (reportType === 'orders') {
      reportService.downloadOrdersCSV(previewData.orders, previewData.summary, filename);
    } else if (reportType === 'users') {
      setLoading(true);
      setError('');
      setSuccess('');
      try {
        const result = await reportService.getUsersReport(startDate, endDate);
        if (!result.success) {
          setError(result.error || 'Failed to generate report');
          return;
        }
        setPreviewData(result.data);
        reportService.downloadUsersCSV(result.data.users, result.data.summary, filename);
        setSuccess('Report downloaded successfully!');
      } catch {
        setError('Failed to download report');
      } finally {
        setLoading(false);
      }
      return;
    } else if (reportType === 'total-sales') {
      reportService.downloadTotalSalesCSV(previewData.orders, previewData.summary, filename);
    } else if (reportType === 'bmf-coins') {
      reportService.downloadBMFCoinsCSV(previewData.transactions, previewData.summary, filename);
    } else if (reportType === 'refunds') {
      reportService.downloadRefundsCSV(previewData.refunds, previewData.summary, filename);
    }
    setSuccess('Report downloaded successfully!');
  };

  const handleClear = () => {
    setStartDate('');
    setEndDate('');
    setPreviewData(null);
    setError('');
    setSuccess('');
  };

  // Set quick date ranges
  const setQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Reports</h1>
          <p className="text-gray-600">Generate and download reports for orders, users, and refunds</p>
        </div>
      </div>

      {/* Report Configuration */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#548B2E]" />
          Generate Report
        </h2>

        {/* Report Type Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Report Type</label>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <button
              onClick={() => { setReportType('orders'); setPreviewData(null); }}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                reportType === 'orders'
                  ? 'border-[#548B2E] bg-[#f2f8eb] text-[#2e4e18]'
                  : 'border-gray-200 hover:border-[#548B2E] text-gray-600'
              }`}
            >
              <ShoppingCart className="w-5 h-5 flex-shrink-0" />
              <div className="text-left">
                <p className="font-medium">Orders & Sales</p>
                <p className="text-xs opacity-70">Subtotal, GST, net sales</p>
              </div>
            </button>
            <button
              onClick={() => { setReportType('refunds'); setPreviewData(null); }}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                reportType === 'refunds'
                  ? 'border-[#548B2E] bg-[#f2f8eb] text-[#2e4e18]'
                  : 'border-gray-200 hover:border-[#548B2E] text-gray-600'
              }`}
            >
              <Receipt className="w-5 h-5 flex-shrink-0 text-red-600" />
              <div className="text-left">
                <p className="font-medium">Refund Report</p>
                <p className="text-xs opacity-70">Bank account monetary refunds</p>
              </div>
            </button>
            <button
              onClick={() => { setReportType('users'); setPreviewData(null); }}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                reportType === 'users'
                  ? 'border-[#548B2E] bg-[#f2f8eb] text-[#2e4e18]'
                  : 'border-gray-200 hover:border-[#548B2E] text-gray-600'
              }`}
            >
              <Users className="w-5 h-5 flex-shrink-0" />
              <div className="text-left">
                <p className="font-medium">Users Report</p>
                <p className="text-xs opacity-70">Registrations, roles, activity</p>
              </div>
            </button>
            <button
              onClick={() => { setReportType('bmf-coins'); setPreviewData(null); }}
              className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                reportType === 'bmf-coins'
                  ? 'border-[#548B2E] bg-[#f2f8eb] text-[#2e4e18]'
                  : 'border-gray-200 hover:border-[#548B2E] text-gray-600'
              }`}
            >
              <Coins className="w-5 h-5 flex-shrink-0" />
              <div className="text-left">
                <p className="font-medium">BMF Coins</p>
                <p className="text-xs opacity-70">Credited & used coins</p>
              </div>
            </button>
          </div>
        </div>

        {/* Date Range Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="w-4 h-4 inline mr-1" />
            Date Range
          </label>
          <div className="flex flex-wrap gap-4 mb-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#548B2E] focus:border-transparent"
              />
            </div>
          </div>
          
          {/* Quick Date Range Buttons */}
          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-gray-500">Quick select:</span>
            <button
              onClick={() => setQuickRange(7)}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
            >
              Last 7 days
            </button>
            <button
              onClick={() => setQuickRange(30)}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
            >
              Last 30 days
            </button>
            <button
              onClick={() => setQuickRange(90)}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
            >
              Last 90 days
            </button>
            <button
              onClick={() => setQuickRange(365)}
              className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
            >
              Last year
            </button>
            <button
              onClick={handleClear}
              className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Clear dates
            </button>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}
        {success && !previewData && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
            <CheckCircle className="w-5 h-5" />
            {success}
          </div>
        )}

        {/* Generate Button */}
        <button
          onClick={handleGenerateReport}
          disabled={loading}
          className="flex items-center gap-2 px-6 py-3 bg-[#548B2E] hover:bg-[#3f6b20] text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <FileText className="w-5 h-5" />
          )}
          {loading ? 'Generating...' : 'Generate Report'}
        </button>
      </div>

      {/* Preview & Download */}
      {previewData && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#548B2E]" />
              Report Preview
            </h2>
            <button
              onClick={handleDownload}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-[#2e4e18] hover:bg-[#3f6b20] text-white rounded-lg font-medium transition-colors"
            >
              <Download className="w-5 h-5" />
              Download CSV
            </button>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {(reportType === 'orders' || reportType === 'total-sales') ? (
              <>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-600">Total Orders</p>
                  <p className="text-2xl font-bold text-blue-700">{previewData.summary.totalOrders || 0}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-600">Gross Sales</p>
                  <p className="text-2xl font-bold text-green-700 flex items-center">
                    <IndianRupee className="w-5 h-5" />
                    {Number(previewData.summary.grossSalesAmount || 0).toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="bg-rose-50 rounded-lg p-4">
                  <p className="text-sm text-rose-600">Total Discounts / Savings</p>
                  <p className="text-2xl font-bold text-rose-700 flex items-center">
                    <IndianRupee className="w-5 h-5" />
                    {Number(previewData.summary.totalDiscounts || 0).toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="bg-teal-50 rounded-lg p-4">
                  <p className="text-sm text-teal-600">Delivery Charges</p>
                  <p className="text-2xl font-bold text-teal-700 flex items-center">
                    <IndianRupee className="w-5 h-5" />
                    {Number(previewData.summary.totalDeliveryCharges || 0).toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-4">
                  <p className="text-sm text-indigo-600">Total GST</p>
                  <p className="text-2xl font-bold text-indigo-700 flex items-center">
                    <IndianRupee className="w-5 h-5" />
                    {formatPaise(previewData.summary.totalGST)}
                  </p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-4">
                  {/* Cash only — a COD order collected on the UPI QR counts as
                      online revenue, since that money reached the bank. Calling
                      this "COD" would overstate the notes an agent hands in. */}
                  <p className="text-sm text-emerald-600">Cash Revenue</p>
                  <p className="text-2xl font-bold text-emerald-700 flex items-center">
                    <IndianRupee className="w-5 h-5" />
                    {Number(
                      previewData.summary.cashRevenue !== undefined
                        ? previewData.summary.cashRevenue
                        : previewData.summary.totalWalletPaid !== undefined
                        ? (previewData.summary.netSalesAmount || previewData.summary.totalRevenue || 0) - (previewData.summary.totalOnlinePaid || 0)
                        : 0
                    ).toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="bg-sky-50 rounded-lg p-4">
                  <p className="text-sm text-sky-600">Online Revenue</p>
                  <p className="text-2xl font-bold text-sky-700 flex items-center">
                    <IndianRupee className="w-5 h-5" />
                    {Number(
                      previewData.summary.onlineRevenue !== undefined
                        ? previewData.summary.onlineRevenue
                        : previewData.summary.totalOnlinePaid || 0
                    ).toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4">
                  <p className="text-sm text-amber-600">Total BMF Coins</p>
                  <p className="text-2xl font-bold text-amber-700 flex items-center">
                    <IndianRupee className="w-5 h-5" />
                    {Number(
                      previewData.summary.bmfCoinsRedeemed !== undefined
                        ? previewData.summary.bmfCoinsRedeemed
                        : previewData.summary.totalWalletPaid || 0
                    ).toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-purple-600">Net Sales</p>
                  <p className="text-2xl font-bold text-purple-700 flex items-center">
                    <IndianRupee className="w-5 h-5" />
                    {Number(previewData.summary.netSalesAmount || previewData.summary.totalRevenue || 0).toLocaleString('en-IN')}
                  </p>
                </div>
              </>
            ) : reportType === 'refunds' ? (
              <>
                <div className="bg-red-50 rounded-lg p-4 col-span-2">
                  <p className="text-sm text-red-600">Total Monetary Refunds</p>
                  <p className="text-2xl font-bold text-red-700">{previewData.summary.totalRefunds || 0}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 col-span-2">
                  <p className="text-sm text-amber-600">Total Refunded Amount</p>
                  <p className="text-2xl font-bold text-amber-700 flex items-center">
                    <IndianRupee className="w-5 h-5" />
                    {Number(previewData.summary.totalRefundAmount || 0).toLocaleString('en-IN')}
                  </p>
                </div>
              </>
            ) : reportType === 'bmf-coins' ? (
              <>
                <div className="bg-blue-50 rounded-lg p-4 col-span-2">
                  <p className="text-sm text-blue-600">Total BMF Coins Credited</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {Number(previewData.summary.totalCredited).toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 col-span-2">
                  <p className="text-sm text-green-600">Total BMF Coins Redeemed</p>
                  <p className="text-2xl font-bold text-green-700">
                    {Number(previewData.summary.totalRedeemed).toLocaleString('en-IN')}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-600">Total Users</p>
                  <p className="text-2xl font-bold text-blue-700">{previewData.summary.totalUsers}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-600">Active Users</p>
                  <p className="text-2xl font-bold text-green-700">{previewData.summary.activeUsers}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <p className="text-sm text-orange-600">Admin Users</p>
                  <p className="text-2xl font-bold text-orange-700">{previewData.summary.adminUsers}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-purple-600">Inactive</p>
                  <p className="text-2xl font-bold text-purple-700">{previewData.summary.statusCounts?.inactive || 0}</p>
                </div>
              </>
            )}
          </div>

          {/* Data Preview Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  {(reportType === 'orders' || reportType === 'total-sales') ? (
                    <>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Order ID</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Order Date & Time</th>
                      {/* BMF-146. Next to the order date so the pair reads as
                          ordered-then-delivered. Date only: the time an order
                          was handed over is not what this report is read for. */}
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Delivery Date</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Customer Name</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Delivery Agent</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Order Value (Subtotal)</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Discount</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Delivery Charges</th>
                      {/* BMF-144. Order Value - Discount + Delivery, then the
                          base the GST was derived out of, then the GST — so the
                          three sit in the order the arithmetic runs. */}
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Total Order Value</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Taxable Amount</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">GST Amount</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Net Paid</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">BMF Coins / Wallet Paid</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Payment Method</th>
                      {/* "Payment Mode" is the ticket's wording for it. The
                          field stays payment_instrument end to end. */}
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Payment Mode</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Order Status</th>
                    </>
                  ) : reportType === 'refunds' ? (
                    <>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Order ID</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Customer Name</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Date of Refund</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Amount of Refund</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                    </>
                  ) : reportType === 'bmf-coins' ? (
                    <>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Customer</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Mobile / User ID</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Coins Credited</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Coins Redeemed</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Category</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Transaction Ref ID</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Description</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Date</th>
                    </>
                  ) : (
                    <>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">User ID</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Name</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Email</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Role</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Registered</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(reportType === 'orders' || reportType === 'total-sales')
                  ? previewData.orders.slice(0, 10).map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{order.orderNumber || `#${order.id}`}</td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {new Date(order.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                        </td>
                        {/* Muted dash rather than a blank cell: an order that is
                            not delivered yet has a real state worth seeing, and
                            an empty cell reads as missing data. */}
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {order.deliveredAt
                            ? new Date(order.deliveredAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })
                            : <span className="text-gray-400">&mdash;</span>}
                        </td>
                        <td className="px-4 py-3">{order.customerName}</td>
                        {/* Muted when nobody is on it yet, so a real name stands
                            out against the orders still waiting for an agent. */}
                        <td className={`px-4 py-3 whitespace-nowrap ${order.agentName && order.agentName !== 'Unassigned' ? '' : 'text-gray-400'}`}>
                          {order.agentName || 'Unassigned'}
                        </td>
                        <td className="px-4 py-3">₹{Number(order.productSubtotal || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-rose-600 font-medium">₹{Number(order.discountAmount || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3">₹{Number(order.deliveryFee || 0).toLocaleString('en-IN')}</td>
                        {/* To the paisa, unlike the columns above: these are tax
                            figures that have to be seen to add up, and en-IN's
                            default would otherwise print three decimals on some
                            rows and none on others. */}
                        <td className="px-4 py-3 font-medium">₹{formatPaise(order.totalOrderValue)}</td>
                        <td className="px-4 py-3">₹{formatPaise(order.taxableAmount)}</td>
                        <td className="px-4 py-3">₹{formatPaise(order.gstAmount)}</td>
                        <td className="px-4 py-3 font-medium text-emerald-600">
                          {/* BMF-157. `|| totalAmount` here would read a real
                              zero as a missing figure: a split order whose
                              whole online leg was refunded genuinely has ₹0
                              left paid in money, and falling back would print
                              the full order value as if it were still paid.
                              Only reach for the total when the backend sent no
                              split at all — a legacy order it could not
                              resolve. The CSV column already sums the two
                              without a fallback, so this also stops the export
                              and the screen disagreeing. */}
                          ₹{(order.cashAmount != null || order.onlineAmount != null
                              ? Number(order.cashAmount || 0) + Number(order.onlineAmount || 0)
                              : Number(order.totalAmount || 0)).toLocaleString('en-IN')}
                        </td>
                        <td className="px-4 py-3 text-amber-600">₹{Number(order.walletCoinsAmount || order.walletAmount || 0).toLocaleString('en-IN')}</td>
                        {/* Method and instrument answer different questions —
                            how the order was placed, and what the money moved
                            on — so they get a column each rather than one
                            "COD (UPI)" string a spreadsheet cannot group by. */}
                        <td className="px-4 py-3 text-xs font-semibold">{order.paymentMethod ? getMethodName(order.paymentMethod) : 'N/A'}</td>
                        {/* Muted dash, not 'N/A': an order can genuinely be paid
                            on nothing yet, which is not missing data. */}
                        <td className={`px-4 py-3 text-xs font-semibold whitespace-nowrap ${order.paymentInstrument ? '' : 'text-gray-400'}`}>
                          {getInstrumentName(order.paymentInstrument) || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {order.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  : reportType === 'refunds'
                  ? previewData.refunds.slice(0, 10).map((refund) => (
                      <tr key={refund.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{refund.orderNumber || `#${refund.orderId}`}</td>
                        <td className="px-4 py-3 font-medium text-gray-800">{refund.customerName}</td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {new Date(refund.refundDate).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                        </td>
                        <td className="px-4 py-3 font-semibold text-red-600">
                          ₹{Number(refund.amount || 0).toLocaleString('en-IN')}
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 uppercase">
                            {refund.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  : reportType === 'bmf-coins'
                  ? previewData.transactions.slice(0, 10).map((txn) => (
                      <tr key={txn.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">{txn.userName}</td>
                        <td className="px-4 py-3 text-gray-600">{txn.phone} / #{txn.userId}</td>
                        <td className="px-4 py-3 text-green-600 font-medium">
                          {txn.creditedAmount !== null && txn.creditedAmount !== undefined ? `+${txn.creditedAmount}` : '-'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {txn.redeemedAmount !== null && txn.redeemedAmount !== undefined ? txn.redeemedAmount : '-'}
                        </td>
                        <td className="px-4 py-3 uppercase text-xs font-semibold text-gray-600">{txn.type || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{txn.category || '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{txn.transactionRefId && txn.transactionRefId !== 'NONE' ? txn.transactionRefId : '-'}</td>
                        <td className="px-4 py-3 text-gray-600">{txn.description || '-'}</td>
                        <td className="px-4 py-3 text-gray-500">
                          {new Date(txn.createdAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                        </td>
                      </tr>
                    ))
                  : previewData.users.slice(0, 10).map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium">#{user.id}</td>
                        <td className="px-4 py-3">{user.name}</td>
                        <td className="px-4 py-3">{user.email}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.role === 'super_admin' ? 'bg-amber-100 text-amber-700'
                              : user.role === 'admin' ? 'bg-purple-100 text-purple-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {user.role === 'super_admin' ? 'Master Control Admin'
                              : user.role === 'admin' ? 'Normal Admin'
                              : user.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {new Date(user.createdAt).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                        </td>
                      </tr>
                    ))
                }
              </tbody>
            </table>
            {((reportType === 'orders' && previewData.orders.length > 10) ||
              (reportType === 'total-sales' && previewData.orders.length > 10) ||
              (reportType === 'bmf-coins' && previewData.transactions.length > 10) ||
              (reportType === 'users' && previewData.users.length > 10)) && (
              <p className="text-center text-sm text-gray-500 py-3 border-t">
                Showing first 10 of {
                  reportType === 'orders' || reportType === 'total-sales'
                    ? previewData.orders.length
                    : reportType === 'bmf-coins'
                    ? previewData.transactions.length
                    : previewData.users.length
                } records. Download CSV for complete data.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportManagement;
